import { Entity } from "game-common/models"
import { BonecallerAttack } from "game-common/bonecaller_combat"
import { AbstractRenderer, AnimatedSprite, BLEND_MODES, Graphics } from "pixi.js"
import { ScalingParams } from "../../../../client_util"
import { bakeRitualFlameFrames } from "../sprites/ritual_flame"
import { getRendererApp } from "../renderer_app"

export const BONECALLER_BOUNDARY_FLAMES = 24
export const BONECALLER_LIGHTNING_MS = 1000
export const BONECALLER_FLAME_FADE_MS = 600
export const BONECALLER_CIRCLE_OPEN_MS = 500
export const BONECALLER_CIRCLE_FADE_MS = 500

// One palette for the whole encounter: grave bone, the ritual's venom green, and the violet the
// shrine and its stones already burn with. Every piece of caster art mixes from these, so the
// telegraphs, the staff and the shrine read as one spell rather than three overlays.
const INK = {
    bone: 0xf3e9cf,
    boneShade: 0x8e7d5d,
    boneDim: 0xbda87f,
    venom: 0x9ff55c,
    venomPale: 0xdcffb0,
    violet: 0x8539c9,
    plum: 0x6a306c,
    orchid: 0x8436b4,
    shadow: 0x120d18,
    crack: 0x160f1d,
    staff: 0x241d2e,
    white: 0xffffff,
}

// Where the ground splits before a grave spike erupts. Deliberately NOT evenly spaced: six spokes
// at 60 degrees drew a wagon wheel, which is what this art used to look like.
const CRACK_ANGLES = [0.4, 1.55, 2.5, 3.6, 5.1]

// The shards that stand up on impact, as (x, y) fractions of the mark's radius plus a height and a
// half-width in world pixels. A fixed scatter rather than a ring: bone coming out of the ground is
// not symmetrical, and anything symmetrical reads as a wheel again.
const SHARDS: [number, number, number, number][] = [
    [-0.62, 0.26, 22, 6], [-0.26, -0.4, 16, 5], [0.18, 0.4, 28, 7], [0.6, -0.08, 20, 6],
    [0.82, 0.3, 12, 4], [-0.86, -0.06, 12, 4], [-0.02, 0.04, 33, 8], [0.38, -0.46, 11, 3.5],
]

/**
 * The ground mark the server has committed to, as this client last heard it. Held across frames so
 * the mark can close over BONECALLER_CIRCLE_FADE_MS after the attack is gone, rather than vanishing
 * on the message that ends it -- and so the closing animation keeps drawing the SAME geometry the
 * hit test used, never a locally invented one.
 */
interface SpikeMark {
    x: number
    y: number
    radius: number
    inner: number
    color: number
    strokeAlpha: number
    fillAlpha: number
    expiresAt: number
    progress: number
    impact: boolean
}

/** Ground-space geometry on the overlay surface, above scenery and below HUD text. */
export class BonecallerWarning extends Graphics {
    private attack?: BonecallerAttack
    private receivedAt = 0
    private caster = { x: 0, y: 0 }
    private combatRadius = 0
    private flames: AnimatedSprite[] = []
    private fadeFrom = 1
    private fadeTo = 1
    private fadeStartedAt = 0
    private fadingCircle?: SpikeMark
    private circleFadeStartedAt?: number
    /** How far a body at a world point is drawn up for the hill there (hill_lift.ts bodyLiftAt); 0 off a hill. */
    private liftAt: (x: number, y: number) => number = () => 0
    /** The caster's own lift: the warning is drawn about it, and every mark is set off from it by its own ground's. */
    private lift = 0

    constructor(renderer: AbstractRenderer = getRendererApp().renderer) {
        super()
        const frames = bakeRitualFlameFrames(renderer)
        for (let i = 0; i < BONECALLER_BOUNDARY_FLAMES; i++) {
            const flame = new AnimatedSprite(frames, false)
            flame.anchor.set(0.5, 1)
            flame.scale.set(0.32)
            flame.blendMode = BLEND_MODES.ADD
            this.addChild(flame)
            this.flames.push(flame)
        }
    }

    update = (entity: Entity, now = Date.now(), liftAt?: (x: number, y: number) => number) => {
        if (liftAt) this.liftAt = liftAt
        if (this.attack !== entity.bonecallerAttack) this.receivedAt = now
        this.attack = entity.bonecallerAttack
        this.combatRadius = entity.bonecallerCombatRadius || 0
        this.caster = { ...entity.location }
        this.animate(now)
    }

    viewerInside = (point: { x: number; y: number }) =>
        Math.hypot(point.x - this.caster.x, point.y - this.caster.y) <= this.combatRadius

    setBoundaryVisible = (visible: boolean, now = Date.now()) => {
        const target = visible ? 1 : 0
        if (target === this.fadeTo) return
        this.fadeFrom = this.boundaryOpacity(now)
        this.fadeTo = target
        this.fadeStartedAt = now
    }

    private boundaryOpacity = (now: number) => {
        const t = Math.max(0, Math.min(1, (now - this.fadeStartedAt) / BONECALLER_FLAME_FADE_MS))
        const smooth = t * t * (3 - 2 * t)
        return this.fadeFrom + (this.fadeTo - this.fadeFrom) * smooth
    }

    animate = (now = Date.now(), showWarnings = true) => {
        this.clear()
        const opacity = this.boundaryOpacity(now)
        this.visible = showWarnings || opacity > 0
        const scale = ScalingParams.surfaceZoom
        // Up a hill, about the caster as it is drawn; each mark below stands on its own ground (openspec world/hills).
        this.lift = this.liftAt(this.caster.x, this.caster.y)
        this.position.set(this.caster.x * scale, (this.caster.y - this.lift) * scale)
        this.scale.set(scale)
        // Shared baked particles, with staggered phases. Manual playback avoids 24 ticker listeners
        // and pauses naturally when the encounter is culled. No boundary strokes remain.
        this.flames.forEach((flame, i) => {
            const a = i * Math.PI * 2 / this.flames.length
            const x = Math.cos(a) * this.combatRadius, y = Math.sin(a) * this.combatRadius
            // Each flame on the ground under it, so the circle drapes over a knoll's tiers and slopes.
            flame.position.set(x, y - this.raise(x, y))
            flame.alpha = opacity
            flame.gotoAndStop((Math.floor(now * 24 / 1000) + i * 7) % flame.textures.length)
        })
        // Culled caster warnings disappear immediately, but its flames finish their own fade.
        if (!showWarnings) return
        const attack = this.attack
        if (!attack) { this.fadeCircle(now); return }
        const remaining = Math.max(0, attack.remainingMs - (now - this.receivedAt))
        const progress = Math.min(1, 1 - remaining / attack.durationMs)
        if (remaining === 0) { this.fadeCircle(now); return }
        const top = -35 - progress * 12
        if (attack.phase === "anticipation" || attack.phase === "windup") {
            this.drawChannel(top, progress, now)
        }
        if (attack.phase === "anticipation" || attack.phase === "recovery") { this.fadeCircle(now); return }
        if (attack.kind !== "spike") this.fadeCircle(now)
        // A stale warning must never invent a local impact: only the server publishes impact.
        const x = attack.x - this.caster.x
        const y = attack.y - this.caster.y - this.raise(x, attack.y - this.caster.y)
        const impact = attack.phase === "impact"
        if (attack.kind === "spike") {
            const opening = impact ? 1 : Math.max(0, Math.min(1,
                (attack.durationMs - remaining) / BONECALLER_CIRCLE_OPEN_MS))
            const reveal = opening * opening * (3 - 2 * opening)
            const radius = attack.radius * reveal
            const inner = impact ? 3 : radius * (0.85 - 0.65 * progress)
            this.circleFadeStartedAt = undefined
            this.fadingCircle = { x: attack.x, y: attack.y, radius, inner,
                color: impact ? INK.white : INK.bone, strokeAlpha: (impact ? 1 : 0.7 + 0.3 *
                    Math.sin(progress * Math.PI * 8) ** 2) * reveal,
                fillAlpha: (impact ? 0.4 : 0.16) * reveal,
                expiresAt: this.receivedAt + attack.remainingMs, progress, impact }
            this.drawSpikeMark(this.fadingCircle, 1, now)
        } else {
            this.drawSweep(attack, progress, impact, now)
        }
        // Drawn last so the bolt lands ON the mark it is aimed at, never under it.
        if (attack.kind === "spike" && attack.phase === "windup" && remaining <= BONECALLER_LIGHTNING_MS) {
            this.drawLightning(x, y, top, attack.id, attack.durationMs - remaining)
        }
    }

    private fadeCircle = (now: number) => {
        const circle = this.fadingCircle
        if (!circle) return
        if (this.circleFadeStartedAt === undefined) this.circleFadeStartedAt = Math.min(now, circle.expiresAt)
        const t = Math.max(0, Math.min(1, (now - this.circleFadeStartedAt) / BONECALLER_CIRCLE_FADE_MS))
        if (t === 1) { this.fadingCircle = undefined; return }
        const closing = 1 - t * t * (3 - 2 * t)
        this.drawSpikeMark({ ...circle, radius: circle.radius * closing, inner: circle.inner * closing },
            closing, now, true)
    }

    /**
     * The grave-spike ground mark: a rune circle whose glyphs turn, ground cracks that reach the rim
     * as the windup runs out, and a bone ring that closes on the centre -- then, on impact, shards
     * standing up out of the dirt.
     *
     * The first circle drawn here is the server's own geometry, at the server's radius and with the
     * stroke and fill the countdown is encoded in; everything else is decoration laid around it.
     */
    /** How much higher than the caster's ground the ground at (dx, dy) from it is drawn. */
    private raise = (dx: number, dy: number) => this.liftAt(this.caster.x + dx, this.caster.y + dy) - this.lift

    private drawSpikeMark = (mark: SpikeMark, opacity: number, now: number, closing = false) => {
        const x = mark.x - this.caster.x
        const y = mark.y - this.caster.y - this.raise(x, mark.y - this.caster.y)
        const r = mark.radius
        this.lineStyle(0)
        this.beginFill(INK.shadow, (mark.impact ? 0.22 : 0.38) * opacity)
        this.drawEllipse(x, y, r * 1.08, r * 0.94)
        this.endFill()
        this.lineStyle(3, mark.color, mark.strokeAlpha * opacity)
        this.beginFill(INK.plum, mark.fillAlpha * opacity)
        this.drawCircle(x, y, r)
        this.endFill()
        this.lineStyle(0)
        this.beginFill(INK.orchid, (mark.impact ? 0.28 : 0.1) * opacity)
        this.drawCircle(x, y, r * 0.58)
        this.endFill()
        this.lineStyle(8, INK.violet, (mark.impact ? 0.2 : 0.1) * opacity)
        this.drawCircle(x, y, r + 2)
        if (mark.impact) {
            this.lineStyle(2, INK.white, 0.3 * opacity)
            this.drawCircle(x, y, r * 1.2)
        } else {
            this.runeRing(x, y, r, now / 2600, INK.bone, 0.9 * opacity)
        }
        const reach = mark.impact ? 0.95 : 0.9 * mark.progress
        for (let i = 0; i < CRACK_ANGLES.length; i++) {
            this.crack(x, y, CRACK_ANGLES[i], r * reach, i + 1, INK.crack, mark.impact ? 3.6 : 3.2,
                (mark.impact ? 0.85 : 0.75) * opacity)
            if (!mark.impact) {
                this.crack(x, y, CRACK_ANGLES[i], r * reach, i + 1, INK.venom, 1.3,
                    (0.35 + 0.5 * mark.progress) * opacity)
            }
        }
        if (!mark.impact) {
            this.dashedRing(x, y, mark.inner, 10, 0.42, 2, INK.bone, (0.5 + 0.45 * mark.progress) * opacity, now / 900)
            this.lineStyle(0)
            this.beginFill(INK.venom, (0.16 + 0.3 * mark.progress) * opacity)
            this.drawCircle(x, y, 3 + 7 * mark.progress)
            this.endFill()
            this.beginFill(INK.venomPale, (0.4 + 0.5 * mark.progress) * opacity)
            this.drawCircle(x, y, 1.4 + 3 * mark.progress)
            this.endFill()
            return
        }
        this.lineStyle(0)
        this.beginFill(INK.venomPale, 0.14 * opacity)
        this.drawEllipse(x, y + r * 0.12, r * 0.8, r * 0.26)
        this.endFill()
        // The shards only belong to the impact itself: a mark that is closing has nothing left to
        // stand up, and drawing them shrinking looks like the ground swallowing them again.
        if (closing) return
        for (const [dx, dy, height, halfWidth] of SHARDS) {
            this.shard(x + dx * r, y + dy * r * 0.78, height, halfWidth, dx * 3, opacity)
        }
        this.beginFill(INK.white, 0.85 * opacity)
        this.drawCircle(x, y, 3.5)
        this.endFill()
    }

    /** A single bone shard standing out of the ground, lit from the left. */
    private shard = (bx: number, by: number, height: number, halfWidth: number, lean: number, alpha: number) => {
        const tx = bx + lean, ty = by - height
        this.lineStyle(0)
        this.beginFill(INK.boneShade, alpha)
        this.drawPolygon([bx + halfWidth * 0.1, by, tx, ty, bx + halfWidth, by + 1])
        this.endFill()
        this.beginFill(INK.bone, alpha)
        this.drawPolygon([bx - halfWidth, by + 1, tx, ty, bx + halfWidth * 0.1, by])
        this.endFill()
        this.lineStyle(1, INK.staff, alpha * 0.5)
        this.moveTo(bx - halfWidth, by + 1)
        this.lineTo(tx, ty)
        this.lineTo(bx + halfWidth, by + 1)
    }

    /** A jagged split in the ground, drawn flattened into the ground plane. */
    private crack = (x: number, y: number, angle: number, length: number, seed: number,
        color: number, width: number, alpha: number) => {
        if (length <= 0 || alpha <= 0) return
        this.lineStyle(width, color, alpha)
        this.moveTo(x, y)
        for (let s = 1; s <= 4; s++) {
            const t = s / 4, wobble = Math.sin(seed * 7.3 + s * 2.1) * 0.26 * (1 - t)
            this.lineTo(x + Math.cos(angle + wobble) * length * t, y + Math.sin(angle + wobble) * length * t * 0.8)
        }
    }

    /**
     * A turning band of glyphs just inside the rim: tangential strokes between two thin rings, with
     * three longer runes among them. Tangential rather than radial -- radial ticks drew a clock face.
     *
     * Every stroke stays INSIDE the server's radius. The brightest edge a player can see has to be
     * the edge they are actually safe outside of, so nothing decorative may overhang it.
     */
    private runeRing = (cx: number, cy: number, r: number, rot: number, color: number, alpha: number) => {
        if (r <= 0 || alpha <= 0) return
        const band = r * 0.9, inner = r * 0.8
        this.lineStyle(1.6, color, alpha * 0.45)
        this.drawCircle(cx, cy, inner)
        this.lineStyle(3, color, alpha * 0.85)
        for (let i = 0; i < 11; i++) {
            const a0 = rot + i * (Math.PI * 2 / 11), span = 0.12 + 0.09 * Math.abs(Math.sin(i * 2.7))
            this.moveTo(cx + Math.cos(a0) * band, cy + Math.sin(a0) * band)
            this.arc(cx, cy, band, a0, a0 + span)
        }
        this.lineStyle(2, color, alpha * 0.95)
        for (let i = 0; i < 3; i++) {
            const a = rot * -0.6 + i * 2.094
            const px = cx + Math.cos(a) * band, py = cy + Math.sin(a) * band
            const ox = Math.cos(a), oy = Math.sin(a), tx = -oy, ty = ox
            this.moveTo(px - ox * 4 + tx * 3, py - oy * 4 + ty * 3)
            this.lineTo(px + ox * 4, py + oy * 4)
            this.lineTo(px - ox * 4 - tx * 3, py - oy * 4 - ty * 3)
        }
    }

    /** A broken ring: `count` arcs with `gap` of each step left empty, turned by `rot`. */
    private dashedRing = (cx: number, cy: number, r: number, count: number, gap: number,
        width: number, color: number, alpha: number, rot: number) => {
        if (r <= 0 || alpha <= 0) return
        this.lineStyle(width, color, alpha)
        const step = Math.PI * 2 / count
        for (let i = 0; i < count; i++) {
            const a0 = rot + i * step, a1 = a0 + step * (1 - gap)
            this.moveTo(cx + Math.cos(a0) * r, cy + Math.sin(a0) * r)
            this.arc(cx, cy, r, a0, a1)
        }
    }

    /**
     * The bone sweep: a fan at the caster's feet whose swept side brightens as the blow travels
     * across it. On impact three claw trails sweep across the fan, shedding bone chips
     * that fly outward and fade over the authoritative impact phase.
     */
    private drawSweep = (attack: BonecallerAttack, progress: number, impact: boolean, now: number) => {
        const s0 = attack.angle - attack.halfArc, s1 = attack.angle + attack.halfArc
        const r = attack.radius
        const opacity = impact ? 1 - progress : 1
        this.lineStyle(0)
        this.beginFill(INK.shadow, (impact ? 0.18 : 0.32) * opacity)
        this.wedge(r * 1.05, s0 - 0.05, s1 + 0.05)
        this.endFill()
        this.beginFill(impact ? 0xa468d8 : INK.plum, (impact ? 0.2 : 0.16) * opacity)
        this.wedge(r, s0, s1)
        this.endFill()
        this.beginFill(impact ? 0xc9a2ff : INK.orchid, (impact ? 0.15 : 0.1) * opacity)
        this.wedge(r * 0.62, s0, s1)
        this.endFill()
        if (!impact) {
            const lead = s0 + 2 * attack.halfArc * progress
            this.beginFill(INK.venom, 0.14)
            this.wedge(r, s0, lead)
            this.endFill()
            this.lineStyle(8, INK.violet, 0.12)
            this.arcAt(r, s0, s1)
            this.lineStyle(1.6, INK.bone, 0.5)
            this.arcAt(r, s0, s1)
            this.lineStyle(1.4, INK.bone, 0.35)
            for (const edge of [s0, s1]) {
                this.moveTo(0, 0)
                this.lineTo(Math.cos(edge) * r, Math.sin(edge) * r)
            }
            this.lineStyle(3, INK.venomPale, 0.9)
            this.arcAt(r, s0, lead)
            this.lineStyle(2.4, INK.venomPale, 0.95)
            this.moveTo(Math.cos(lead) * r * 0.2, Math.sin(lead) * r * 0.2)
            this.lineTo(Math.cos(lead) * r * 1.04, Math.sin(lead) * r * 1.04)
            this.lineStyle(0)
            this.beginFill(INK.venomPale, 0.9)
            this.drawCircle(Math.cos(lead) * r, Math.sin(lead) * r, 2.6)
            this.endFill()
            for (let i = 0; i < 5; i++) {
                const a = s0 + (i + 0.5) * (2 * attack.halfArc / 5)
                this.lineStyle(2, INK.bone, a <= lead ? 0.85 : 0.3)
                this.moveTo(Math.cos(a) * r * 0.93, Math.sin(a) * r * 0.93)
                this.lineTo(Math.cos(a) * r * 1.06, Math.sin(a) * r * 1.06)
            }
            return
        }
        // Fast travel followed by a fading follow-through. Progress comes from the server's
        // remaining phase time, so receiving another snapshot cannot restart the swing.
        const travel = Math.min(1, progress / 0.8)
        const lead = s0 + (s1 - s0) * (1 - (1 - travel) ** 2)
        const tail = Math.max(s0, lead - attack.halfArc * 0.75)
        this.lineStyle(10, INK.violet, 0.3 * opacity)
        this.arcAt(r * 0.98, tail, lead)
        this.lineStyle(5, INK.white, opacity)
        this.arcAt(r * 0.98, tail, lead)
        for (let i = 0; i < 3; i++) {
            const f = 0.42 + i * 0.24
            this.lineStyle(4 - i * 0.9, INK.bone, (0.95 - i * 0.18) * opacity)
            this.arcAt(r * f, tail, lead)
        }
        this.lineStyle(2, INK.venomPale, opacity)
        this.moveTo(Math.cos(lead) * r * 0.2, Math.sin(lead) * r * 0.2)
        this.lineTo(Math.cos(lead) * r, Math.sin(lead) * r)
        this.lineStyle(0)
        for (let i = 0; i < 6; i++) {
            const a = s0 + (i + 0.5) * (2 * attack.halfArc / 6)
            if (a > lead) continue
            const releasedAt = 0.8 * (1 - Math.sqrt(1 - (a - s0) / (s1 - s0)))
            const age = Math.max(0, progress - releasedAt)
            const chip = r * (0.72 + 0.18 * ((i * 53) % 5) / 5) + age * 24
            this.beginFill(INK.bone, 0.9 * (1 - age) * opacity)
            this.drawCircle(Math.cos(a) * chip, Math.sin(a) * chip - Math.sin(age * Math.PI) * 8,
                (1.6 + (i % 2)) * (1 - age * 0.5))
            this.endFill()
        }
    }

    private wedge = (r: number, a0: number, a1: number) => {
        this.moveTo(0, 0)
        this.arc(0, 0, r, a0, a1)
        this.lineTo(0, 0)
    }

    private arcAt = (r: number, a0: number, a1: number) => {
        this.moveTo(Math.cos(a0) * r, Math.sin(a0) * r)
        this.arc(0, 0, r, a0, a1)
    }

    /**
     * The caster itself charging: a pool of light gathering at its soles, motes rising out of it,
     * and a staff head that brightens and draws its orbiting sparks in as the blow nears. The staff
     * head is where every bolt is fired from, so it has to be the brightest thing on the caster.
     */
    private drawChannel = (top: number, progress: number, now: number) => {
        this.lineStyle(0)
        this.beginFill(INK.venom, 0.1 + 0.12 * progress)
        this.drawEllipse(0, 20, 11 + 7 * progress, 4.5 + 3 * progress)
        this.endFill()
        this.lineStyle(1.5, INK.venom, 0.3 + 0.35 * progress)
        this.drawEllipse(0, 20, 16 + 9 * progress, 6.5 + 3.5 * progress)
        this.lineStyle(1.4, INK.venomPale, 0.35 + 0.4 * progress)
        for (let i = 0; i < 3; i++) {
            const rise = ((now / 420 + i * 0.333) % 1) * 26, mx = Math.sin(now / 300 + i * 2.1) * 12
            this.moveTo(mx, 20 - rise)
            this.lineTo(mx + 1.5, 20 - rise - 4)
        }
        this.lineStyle(4.5, INK.staff, 0.8)
        this.moveTo(10, -6)
        this.lineTo(10, top + 5)
        this.lineStyle(1.8, INK.boneDim, 0.95)
        this.moveTo(9.2, -6)
        this.lineTo(9.2, top + 5)
        this.lineStyle(0)
        this.beginFill(INK.violet, 0.12 + 0.16 * progress)
        this.drawCircle(10, top, 11 + 9 * progress)
        this.endFill()
        this.beginFill(INK.venom, 0.18 + 0.28 * progress)
        this.drawCircle(10, top, 5.5 + 5 * progress)
        this.endFill()
        this.beginFill(INK.venomPale, 0.95)
        this.drawCircle(10, top, 1.8 + 2.8 * progress)
        this.endFill()
        this.lineStyle(1.5, INK.venom, 0.8)
        for (let i = 0; i < 3; i++) {
            const a = now / 240 + i * 2.094, r = 19 - 13 * progress
            this.moveTo(10 + Math.cos(a) * r, top + Math.sin(a) * r * 0.8)
            this.lineTo(10 + Math.cos(a + 0.6) * (r * 0.66), top + Math.sin(a + 0.6) * (r * 0.66) * 0.8)
        }
    }

    private drawLightning = (x: number, y: number, staffY: number, id: number, elapsedMs: number) => {
        const sx = 10, sy = staffY, dx = x - sx, dy = y - sy
        const length = Math.hypot(dx, dy) || 1
        const frame = Math.floor(elapsedMs / 45)
        const points = [{ x: sx, y: sy }]
        for (let i = 1; i < 8; i++) {
            const t = i / 8
            const jag = Math.sin(id * 17.1 + frame * 13.7 + i * 9.3) * 10 * Math.sin(t * Math.PI)
            points.push({ x: sx + dx * t - dy / length * jag, y: sy + dy * t + dx / length * jag })
        }
        points.push({ x, y })
        // Four passes over ONE path so the halo stays welded to the core: a wide violet bloom, the
        // 7px body the fork test pins, a venom-green channel, and a white-hot filament. Fewer, longer
        // segments than before -- a tight zigzag at 7px mitres itself apart into two scribbles.
        for (const [width, color, alpha] of [[9, INK.violet, 0.16], [7, INK.violet, 0.34],
            [3, INK.venom, 0.85], [1.2, 0xeeffcf, 1]]) {
            this.lineStyle(width, color, alpha)
            this.moveTo(sx, sy)
            for (const point of points.slice(1)) this.lineTo(point.x, point.y)
        }
        this.lineStyle(1.4, INK.venom, 0.6)
        for (const index of [3, 5]) {
            const p = points[index]
            const side = index === 3 ? 1 : -1
            this.moveTo(p.x, p.y)
            this.lineTo(p.x - dy / length * 11 * side, p.y + dx / length * 11 * side)
            this.lineTo(p.x - dy / length * 17 * side + dx / length * 7,
                p.y + dx / length * 17 * side + dy / length * 7)
        }
    }
}
